import { ApiClient } from "./api.ts";

export interface Domain {
    id?: string;
    host: string;
    description: string;
    active: boolean;
    created_at?: string;
    updated_at?: string;
    organization_id?: string;
    verified?: boolean;
    verification_code?: string;
    verified_at?: string;
    ssl_status?: 'none' | 'pending' | 'active' | 'expired';
    ssl_expires_at?: string;
    ssl_issued_at?: string;
    details?: {
        routing?: {
            type: string;
            destinations: string[];
            config?: Record<string, string>;
            weights?: Record<string, number>;
            limits?: Record<string, number>;
            mappings?: Record<string, string>;
        };
        rate_limit?: {
            rate: string;
            limit: number;
            multiplier?: number;
            identifier_type?: string;
            identifier?: string;
        };
        ai_shield?: {
            enabled: boolean;
            mode: string;
        };
    };
}

export class DomainService {
    private static instance: DomainService;
    private api = ApiClient.getInstance();

    private constructor() { }

    public static getInstance(): DomainService {
        if (!DomainService.instance) {
            DomainService.instance = new DomainService();
        }
        return DomainService.instance;
    }

    public async listByOrganization(orgId: string): Promise<Domain[]> {
        return this.api.request<Domain[]>(`/organization/${orgId}/domains`) as Promise<Domain[]>;
    }

    public async create(domain: Domain, orgId: string): Promise<Domain> {
        return this.api.request<Domain>(`/domain`, {
            method: 'PUT',
            body: JSON.stringify(domain)
        }) as Promise<Domain>;
    }

    public async update(id: string, domain: Domain, orgId: string): Promise<Domain> {
        return this.api.request<Domain>(`/domain/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(domain)
        }) as Promise<Domain>;
    }

    public async get(id: string): Promise<Domain> {
        return this.api.request<Domain>(`/domain/${id}`) as Promise<Domain>;
    }

    public async delete(id: string): Promise<void> {
        await this.api.request<void>(`/domain/${id}`, {
            method: 'DELETE'
        });
    }

    public async verify(id: string): Promise<Domain> {
        return this.api.request<Domain>(`/domain/${id}/verify`, {
            method: 'POST',
            toast: { success: 'Domain verification started' }
        }) as Promise<Domain>;
    }

    public async obtainCertificate(id: string): Promise<Domain> {
        return this.api.request<Domain>(`/domain/${id}/certificate`, {
            method: 'PUT',
            toast: { success: 'Certificate request submitted' }
        }) as Promise<Domain>;
    }

    public async getCertificateStatus(id: string): Promise<Domain> {
        return this.api.request<Domain>(`/domain/${id}/certificate`, {
            method: 'GET'
        }) as Promise<Domain>;
    }

    public async renewCertificate(id: string): Promise<Domain> {
        return this.api.request<Domain>(`/domain/${id}/certificate`, {
            method: 'PATCH',
            toast: { success: 'Certificate renewal started' }
        }) as Promise<Domain>;
    }
    public async getConfig(id: string): Promise<string> {
        const response = await this.api.request<Response>(`/domain/${id}/config`, {
            headers: {
                'Accept': 'application/x-yaml'
            }
        }, true);

        if (response instanceof Response) {
            return await response.text();
        }
        return ''; // Should not happen with raw=true
    }

    public async updateConfig(id: string, config: string): Promise<any> {
        return this.api.request<any>(`/domain/${id}/config?strict=true`, {
            method: 'PUT',
            body: config,
            headers: {
                'Content-Type': 'application/x-yaml'
            }
        });
    }
}
